import React, { FunctionComponent } from 'react';
import { Trans } from 'react-i18next';

import classNames from 'classnames';

import { TotalsFragment } from '../../../types/__generated__';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import usePageQuery, { buildQuery } from '../../hooks/usePageQuery';
import parseCurrency from '../../utils/parseCurrency';
import Fragment from './Totals.gql';
import TotalsSkeleton from './TotalsSkeleton';

const FRAGMENT = buildQuery(Fragment);

export const Totals: FunctionComponent = () => {
  const { loading, error, refetch, data = null } = usePageQuery<TotalsFragment>(
    FRAGMENT,
    {
      notifyOnNetworkStatusChange: true,
    },
  );
  const { data: basePageData, error: basePageError } = useBasePageQuery();

  if (loading) return <TotalsSkeleton animated displayRefreshButton={false} />;
  if (basePageError || (error && !data?.targetWithCurrency?.value)) {
    return <TotalsSkeleton onRefresh={refetch} />;
  }

  if (!data || !basePageData) return null;

  const { targetWithCurrency, donations } = data;
  const donationSummary = basePageData?.page?.donationSummary;

  const donationCurrency = donationSummary?.totalAmount?.currencyCode ?? 'GBP';
  const donationValue = (donationSummary?.totalAmount?.value ?? 0) / 100;
  const targetAmount = targetWithCurrency?.value;
  const targetCurrency = targetWithCurrency?.currencyCode ?? 'GBP';
  const directDonationCount = donations?.totalCount;
  const aggregatedDonationsCount = donationSummary?.aggregatedDonationsCount;
  const donationTotalCount = aggregatedDonationsCount ?? directDonationCount;

  return (
    <figcaption>
      <div
        data-qa="totaliser-total"
        className={classNames('jg-h1', 'jg-text-color-branded')}
      >
        {parseCurrency(donationCurrency, donationValue, {
          roundDown: false,
          wholeNumber: true,
        })}
      </div>

      <div data-qa="totaliser-message" className={classNames('jg-text--light')}>
        <div>
          <Trans i18nKey="common.raised"> raised</Trans>
          {Boolean(targetAmount) && !data.fundraisingSettings?.isTargetHidden && (
            <Trans i18nKey="page.totaliser_description">
              {' '}
              of{' '}
              <span
                className={classNames(
                  'jg-text--regular',
                  'jg-text-color-branded',
                )}
              >
                {{
                  targetAmount: parseCurrency(
                    targetCurrency,
                    targetAmount / 100,
                    {
                      roundDown: true,
                      wholeNumber: true,
                    },
                  ),
                }}
              </span>{' '}
              target
            </Trans>
          )}
        </div>
        {Boolean(donationTotalCount) && (
          <Trans
            i18nKey={'page.totaliser_supporters'}
            count={donationTotalCount}
          >
            {' '}
            by{' '}
            <span className={classNames('jg-text--regular')}>
              {{ donationsCount: donationTotalCount }} supporter
            </span>
          </Trans>
        )}
      </div>
    </figcaption>
  );
};
